import React, { Suspense, useState, useCallback, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei'
import { EffectComposer, Bloom, ChromaticAberration, Vignette } from '@react-three/postprocessing'
import { BlendFunction } from 'postprocessing'
import RainbowCoin from './RainbowCoin'
import FadeInCoin from './FadeInCoin'
import Lightning from './Lightning'
import EffectsControls from './EffectsControls'
import ScrollingText3D from './ScrollingText3D'
import VideoBackground from './VideoBackground'

function Scene() {
  const [isMobile, setIsMobile] = useState(false)
  const [effects, setEffects] = useState({
    coinScale: 1,
    rainbowSpeed: 1,
    glowIntensity: 2,
    distortStrength: 0.1,
    textFont: 'Impact',
    textSize: 1.2,
    textSpeed: 1.5,
    textSpacing: 1.0,
    textBoldness: 0.05,
    lightningCount: 8,
    lightningRadius: 4,
    lightningIntensity: 0.8,
    bloomIntensity: 1.5,
    bloomRadius: 0.8,
    chromaticAberration: 0.005,
    vignetteIntensity: 0.5,
    autoRotate: true,
    rotationSpeed: 0.5
  })

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleEffectsChange = useCallback((newEffects) => {
    setEffects(newEffects)
  }, [])

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      position: 'relative',
      backgroundColor: '#000000'
    }}>
      
      {/* <EffectsControls onEffectsChange={handleEffectsChange} /> */}
      
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ backgroundColor: '#000000' }}
        gl={{ 
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          clearColor: '#000000'
        }}
      >
        {/* Dramatic lighting for psychedelic effect */}
        <ambientLight intensity={0.2} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={0.8}
          color="#ff00ff"
        />
        <pointLight position={[-10, 5, -5]} intensity={1} color="#00ffff" />
        <pointLight position={[10, -5, 5]} intensity={1} color="#ffff00" />
        <spotLight 
          position={[0, 10, 0]} 
          intensity={2} 
          color="#ff0080"
          angle={Math.PI / 4}
          penumbra={1}
        />
        
        {/* Video background texture */}
        <VideoBackground />
        
        {/* Dark environment for contrast */}
        <Environment preset="sunset" />
        
        {/* 3D Scrolling background text - temporarily hidden */}
        {/* <ScrollingText3D 
          fontFamily={effects.textFont}
          fontSize={effects.textSize}
          speed={effects.textSpeed}
          spacing={effects.textSpacing}
          boldness={effects.textBoldness}
        /> */}
        
        {/* Suspense for loading */}
        <Suspense fallback={null}>
          <FadeInCoin 
            scale={effects.coinScale}
            rainbowSpeed={effects.rainbowSpeed}
            glowIntensity={effects.glowIntensity}
            position={[0, 0, 0]}
            isMobile={isMobile}
          />
        </Suspense>
        
        {/* Subtle ground reflection */}
        <ContactShadows 
          position={[0, -3, 0]} 
          opacity={0.2} 
          scale={15} 
          blur={2} 
          far={6} 
          color="#ff00ff"
        />
        
        {/* Camera controls - only zoom enabled */}
        <OrbitControls 
          target={[0, 0, 0]}
          enablePan={false} 
          enableZoom={true} 
          enableRotate={false}
          minDistance={2}
          maxDistance={8}
          enableDamping
          dampingFactor={0.05}
        />

        {/* Post-processing effects */}
        <EffectComposer>
          <Bloom
            intensity={effects.bloomIntensity}
            radius={effects.bloomRadius}
            luminanceThreshold={0.1}
            luminanceSmoothing={0.9}
            blendFunction={BlendFunction.ADD}
          />
          <ChromaticAberration
            offset={[effects.chromaticAberration, effects.chromaticAberration]}
            blendFunction={BlendFunction.NORMAL}
          />
          <Vignette
            eskil={false}
            offset={0.1}
            darkness={effects.vignetteIntensity}
            blendFunction={BlendFunction.MULTIPLY}
          />
        </EffectComposer>
      </Canvas>
    </div>
  )
}

export default Scene
